import store from "../redux/store";
import {
  incrementChannelUnread,
  bumpChannelActivity,
} from "../redux/reducer/chatUnreadSlice";
import { getChatGroupsList } from "../redux/actions/streamChatActions";

// Curated "visible" lists — keep in sync with the Chat page / StreamChatBootstrap.
// These define the only channels whose unread counts toward the toolbar badge.
const VISIBLE_LIST_REQUESTS = [
  {
    criteria: { group_types_included: ["public", "regional"], is_member: true },
    page: 1,
    limit: 100,
  },
  {
    criteria: { group_types_included: ["private"], is_member: true },
    page: 1,
    limit: 100,
  },
];

class StreamChatUnreadSync {
  constructor() {
    this.client = null;
    this.unsubscribeFns = [];
    this.refreshTimer = null;
  }

  // A message arrived for a channel we don't yet track (e.g. a brand-new 1:1).
  // Re-fetch the curated visible lists (debounced) so a newly-visible chat
  // starts counting. Hidden/stale chats are never returned by these fetches,
  // so they remain excluded from the badge.
  scheduleVisibleListsRefresh() {
    if (this.refreshTimer) return;
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      VISIBLE_LIST_REQUESTS.forEach((body) => store.dispatch(getChatGroupsList(body)));
    }, 4000);
  }

  resolveEventChannelId(event) {
    const candidates = [
      event?.channel?.id,
      event?.channel_id,
      event?.id,
      event?.cid ? String(event.cid).split(":")[1] : null,
      event?.channel?.cid ? String(event.channel.cid).split(":")[1] : null,
    ];

    for (const candidate of candidates) {
      const value = String(candidate || "").trim();
      if (value) {
        return value;
      }
    }

    return null;
  }

  start(clientInstance) {
    this.stop();

    if (!clientInstance) {
      return;
    }

    this.client = clientInstance;

    // ── Per-channel counts (drive both the toolbar badge and in-list badges) ──
    // The badge is the sum of unread over VISIBLE chats only (see
    // selectTotalUnread). We deliberately ignore Stream's global
    // `total_unread_count` because it bundles in hidden/stale chats. So we only
    // bump a channel that's already part of the curated visible set; an unknown
    // channel triggers a debounced list refresh (which never surfaces hidden
    // chats) instead of being counted blindly.
    const onMessageNew = (event) => {
      const currentUserId = this.client?.userID;
      const messageAuthorId = event?.message?.user?.id || event?.user?.id;

      if (currentUserId && messageAuthorId && currentUserId === messageAuthorId) {
        return;
      }

      const channelId = this.resolveEventChannelId(event);
      if (!channelId) {
        return;
      }

      // Float this chat to the top of the lists/sliders. Bump for every incoming
      // message — including a brand-new chat not yet tracked, so it lands at the
      // top once the debounced list refresh below surfaces it.
      store.dispatch(bumpChannelActivity({ id: channelId, at: Date.now() }));

      const byChannelId = store.getState()?.chatUnread?.byChannelId || {};
      const isKnownVisible = Object.prototype.hasOwnProperty.call(
        byChannelId,
        channelId,
      );
      if (!isKnownVisible) {
        // Could be a brand-new visible 1:1 (count it after refresh) or a
        // hidden/stale chat (refresh won't return it → stays excluded).
        this.scheduleVisibleListsRefresh();
        return;
      }

      store.dispatch(incrementChannelUnread({ id: channelId, by: 1 }));
    };

    // message.new fires for watched channels; notification.message_new fires for
    // member channels the user isn't currently watching — handle both.
    this.client.on("message.new", onMessageNew);
    this.client.on("notification.message_new", onMessageNew);

    this.unsubscribeFns.push(() => this.client?.off("message.new", onMessageNew));
    this.unsubscribeFns.push(() =>
      this.client?.off("notification.message_new", onMessageNew),
    );
  }

  stop() {
    this.unsubscribeFns.forEach((unsubscribe) => {
      try {
        unsubscribe();
      } catch (_error) {
        // No-op: listener might have been already removed by Stream client.
      }
    });
    this.unsubscribeFns = [];
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.client = null;
  }
}

const streamChatUnreadSync = new StreamChatUnreadSync();

export default streamChatUnreadSync;
